import { useChildNodes } from "./use-child-nodes";
import type { LibraryNode } from "../../lib/types";

/**
 * Sprint 21 — aggregate an agent's learnings for the cockpit Learnings panel.
 * Everything is derived from existing primitives (no new tool): feedback
 * memories + retrospective pages under the agent. A "pattern" is a feedback
 * memory whose body carries the pattern marker (set by find_patterns).
 */

/** Mirror of the worker's `PATTERN_MARKER` (retrospectives.ts) — keep in lockstep. */
export const PATTERN_MARKER = "Pattern detected across tasks";

export function isPatternMemory(node: LibraryNode): boolean {
  return node.memory_type === "feedback" && !!node.document && node.document.includes(PATTERN_MARKER);
}

export interface AgentLearnings {
  feedbackMemories: LibraryNode[];
  patterns: LibraryNode[];
  retrospectives: LibraryNode[];
  /** Feedback memories the workforce treats as reliable (confidence > 0.7). */
  highConfidenceCount: number;
  isLoading: boolean;
  refetch: () => void;
}

export function useAgentLearnings(agentId: string | undefined): AgentLearnings {
  const memories = useChildNodes(agentId, "memory");
  const pages = useChildNodes(agentId, "page");

  const mems = memories.data ?? [];
  // Bug 3 (Sprint 27.0): seeded template memories aren't learnings the agent earned.
  const feedbackMemories = mems.filter((m) => m.memory_type === "feedback" && !m.seed);
  const patterns = feedbackMemories.filter(isPatternMemory);
  const retrospectives = (pages.data ?? []).filter((p) => p.surface === "retrospective");
  const highConfidenceCount = feedbackMemories.filter((m) => (m.confidence ?? 1) > 0.7).length;

  return {
    feedbackMemories,
    patterns,
    retrospectives,
    highConfidenceCount,
    isLoading: memories.isLoading || pages.isLoading,
    refetch: () => {
      memories.refetch();
      pages.refetch();
    },
  };
}
